/** Waits for a condition to be true, then resolves the promise. If the condition is not true within the timeout, the promise is rejected.
 * @param fn Function to check
 * @param timeoutMs Max number of milliseconds to wait (default: 1000ms)
 * @param intervalMs Interval in milliseconds between checks (default: 16ms ≈ 1 frame at 60fps)
 *
 * @returns Promise<boolean>
 *
 * @example
 * ```
 * const result = await waitForCondition(() => {
 *  return document.querySelector(".my-class") !== null;
 * }, 1000); // wait up to 1 second
 * ```
 * */
export const waitForCondition = async (
  fn: () => boolean,
  timeoutMs = 1000,
  intervalMs = 16,
): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;

    function doCheckFn() {
      const result = fn();
      if (result) {
        resolve(true);
      } else if (Date.now() >= deadline) {
        reject(new Error("waitForCondition timeout"));
      } else {
        globalThis.setTimeout(doCheckFn, intervalMs);
      }
    }

    doCheckFn();
  });
};

/** @deprecated Use waitForCondition instead */
export const awaiterCycle = waitForCondition;

/**
 * Returns a promise that resolves after a specified number of milliseconds.
 *
 * @param ms - The number of milliseconds to wait before resolving the promise.
 * @returns A promise that resolves after the specified delay.
 *
 * @example
 * ```typescript
 * await awaiter(1000); // Waits for 1 second
 * ```
 */
export async function awaiter(ms: number) {
  return new Promise((resolve) => globalThis.setTimeout(resolve, ms));
}
